import * as React from 'react';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Title, useDataProvider } from 'react-admin';
import Filter from './components/filter';
import { Box } from '@mui/system';
import LinearProgress from '@mui/material/LinearProgress';
import { getDefaultUtcOffset } from '../../utils/common';
import { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import RCBarChart from '../../components/charts/recharts/rcBarChart';
import RCPieChart from '../../components/charts/recharts/rcPieChart';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

// Custom tooltip for the bar chart that displays UnprocessedPrioFast value for "Unprocessed" rows
const CustomBarChartTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        // Extract unprocessedPrioFast from the payload data
        const unprocessedPrioFast = payload[0]?.payload?.unprocessedPrioFast;

        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 50 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{label}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payload.map((row: any) => {
                            // Check if this row is for "Unprocessed" (case-insensitive check)
                            const isUnprocessed = row.name?.toLowerCase() === 'unprocessed';
                            const displayName = isUnprocessed
                                ? `Unprocessed (Prio,Fast: ${unprocessedPrioFast})`
                                : row.name;

                            return (
                                <TableRow key={row.dataKey}>
                                    <TableCell align='right'>
                                        <svg height={6 * 2} width={6 * 2}>
                                            <circle cx={6} cy={6} r={6} style={{ fill: row.fill }} />
                                        </svg>
                                    </TableCell>
                                    <TableCell align="left">{displayName}</TableCell>
                                    <TableCell align="right">{row.value}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
    return null;
};

// Custom tooltip for the pie chart that displays UnprocessedPrioFast value for "Unprocessed" rows
const CustomPieChartTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 40 }} size="small" aria-label="pie chart tooltip">
                    <TableBody>
                        {payload.map((row: any) => {
                            // Check if this row is for "Unprocessed" (case-insensitive check)
                            const isUnprocessed = row.name?.toLowerCase() === 'unprocessed';
                            // Access unprocessedPrioFast from the payload data
                            const unprocessedPrioFast = row.payload?.unprocessedPrioFast;
                            const displayName = isUnprocessed
                                ? `Unprocessed (Prio,Fast: ${unprocessedPrioFast})`
                                : row.name;

                            return (
                                <TableRow key={`pietooltip-${row.name}`}>
                                    <TableCell align='right'>
                                        <svg height={6 * 2} width={6 * 2}>
                                            <circle cx={6} cy={6} r={6} style={{ fill: row.payload.fill }} />
                                        </svg>
                                    </TableCell>
                                    <TableCell align="left">{displayName}</TableCell>
                                    <TableCell align="right">{row.value}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
    return null;
};

// Series list for pie chart (defined outside to be used in convertData)
const pieChartSeriesList = [
    { dataKey: 'processed', name: 'Processed', fill: '#4caf50' },
    { dataKey: 'a_ProcessingFailed', name: 'Assigned Processing Failed', fill: '#f44336' },
    { dataKey: 'unprocessed', name: 'UnProcessed', fill: '#9e9e9e' },
    { dataKey: 'ihd', name: 'IHD', fill: '#ffeb3b' },
    { dataKey: 'general', name: 'General', fill: '#2196f3' },
    { dataKey: 'any', name: 'Any', fill: '#9c27b0' },
    { dataKey: 'processingFailed', name: 'Processing Failed', fill: '#f7554e' },
];

// Custom convertData function for pie chart that includes unprocessedPrioFast in each segment
const pieChartConvertData = (item: any) => pieChartSeriesList.map((serie: any) => ({
    name: serie.name,
    value: item[serie.dataKey],
    fill: serie.fill,
    unprocessedPrioFast: item.unprocessedPrioFast
}));

const UserDashboard = () => {
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [listUsers, setListUsers] = useState<any[]>([]);
    const [chartType, setChartType] = useState('bar');
    const [teamId, setTeamId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const handleSearch = (filter : any) => {
        setIsLoading(true);
        const startDate = filter.period_ge;
        const endDate = filter.period_le;
        const teamId = filter.teamId;
        const utcOffset = getDefaultUtcOffset();
        // if teamid is undefined, remove it from the url
        let url = `GetDataDashboard?startDate=${startDate}&endDate=${endDate}&utcOffset=${utcOffset}`;
        if (teamId !== undefined && teamId !== null) {
            url += `&teamId=${teamId}`;
        }

        dataProvider.fetchData('UserDashboard', url)
            .then((response: any) => {
                if (response && response?.data) {
                    response.data.forEach((item: any) => {
                        item.name += ` (Total: ${item.unprocessed + item.processingFailed + item.a_ProcessingFailed + item.processed + item.ihd + item.general + item.any})`;
                    });
                    setListUsers(response.data);
                } else {
                    notify('Not found any data.', { type: 'success', autoHideDuration: 5000 });
                }
            })
            .catch((err: any) =>
                notify(`Fail to fetch GetDataDashboard with Filter. Reason::: ${err?.message}`, { type: 'error', autoHideDuration: 5000 })
            )
            .finally(_ => setIsLoading(false));
    };

    useEffect(() => {
        dataProvider.fetchData('User', 'GetTeam')
            .then((response: any) => {
                if (response.status !== 200 && response.data) {
                    response.data !== 0 && setTeamId(response.data);
                }
            }).catch((err: any) => {
                notify(`Fail to fetch Team. Reason::: ${err}`, { type: 'error', autoHideDuration: 5000 });
            });
    }, []);

    return (
        <>
            <Title title={'Users Dashboard'}></Title>
            <Box sx={{ width: '100%', mb: 5 }}>
                <Paper elevation={1} sx={{ pl: 1 }} >
                    <Filter type={chartType} setType={setChartType} handleSearch={handleSearch} teamId={teamId} />
                </Paper>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                {isLoading &&
          <LinearProgress color='secondary' />
          || (
              listUsers?.length !== 0 &&
            (chartType === 'pie' &&
              (
                  <Grid size={12} display="flex" justifyContent="center" alignItems="center">
                      <Paper elevation={10} sx={{ padding: '0px 20px 30px 20px', marginBottom: '30px', width: '83%' }}>
                          <Typography sx={{ marginTop: '20px', marginBottom: '25px' }} variant="h3" textAlign={'center'} mb={2}>User Dashboard</Typography>
                          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} >
                              <RCPieChart
                                  data={listUsers}
                                  dataKey={'name'}
                                  height={220}
                                  width={460}
                                  chartSize={100}
                                  elevation={20}
                                  seriesList={pieChartSeriesList}
                                  customTooltip={CustomPieChartTooltip}
                                  convertData={pieChartConvertData}
                              >
                              </RCPieChart>
                          </Grid>
                      </Paper>
                  </Grid>
              )
              ||
              <Grid size={12} display="flex" justifyContent="center" alignItems="center">
                  <Paper elevation={24} sx={{ width: '90%' }}>
                      <Typography sx={{ marginTop: '20px', marginBottom: '25px' }} variant="h3" textAlign={'center'}>User Dashboard</Typography>
                      <RCBarChart
                          width={1600}
                          data={listUsers}
                          yGap={5}
                          barSize={listUsers.length > 8 ? 50 : 70}
                          xAxis={{ dataKey: 'name', label: { value: 'USER', dy: 12 } }}
                          yAxis={{ position: 'insideLeft', label: { value: 'Orders', angle: -90, dx: -10 } }}
                          legend={{ verticalAlign: 'top', height: 60, iconType: 'square' }}
                          showTotal={true}
                          customTooltip={CustomBarChartTooltip}
                          seriesList={[
                              { dataKey: 'processed', name: 'Processed', fill: '#4caf50', stackId: 'a', labelList: { fill: 'white' } },
                              { dataKey: 'a_ProcessingFailed', name: 'Assigned Processing Failed', fill: '#f44336', stackId: 'a', labelList: { fill: 'white' } },
                              { dataKey: 'unprocessed', name: 'UnProcessed', fill: '#9e9e9e', stackId: 'a', labelList: { fill: 'white' } },
                              { dataKey: 'ihd', name: 'IHD', fill: '#eddb42', stackId: 'b', labelList: { fill: 'white' } },
                              { dataKey: 'general', name: 'General', fill: '#2196f3', stackId: 'b', labelList: { fill: 'white' } },
                              { dataKey: 'any', name: 'Any', fill: '#9c27b0', stackId: 'b', labelList: { fill: 'white' } },
                              { dataKey: 'processingFailed', name: 'Processing Failed', fill: '#f7554e', stackId: 'b', labelList: { fill: 'white' } },
                          ]}
                      >
                      </RCBarChart>
                  </Paper>
              </Grid>
            ))}
            </Box>
        </>
    );
};


const UserDashboardPage = {
    name: 'UserDashboard',
    list: UserDashboard
};

export default UserDashboardPage;